import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { all_routes } from "../../Router/all_routes";
import RecordNotFound from "../../core/components/RecordNotFound";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { Card, CardContent } from "@/components/ui/card";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import TemplatePreviewModal from "./templatePreviewModal";
import { Button } from "@/components/ui/button";
import {
  FileText,
  Eye,
  Edit,
  PlusCircle,
  RotateCcw,
  Copy,
  Trash2,
  Receipt,
  Tag,
  BarChart3,
  Mail,
  Wrench,
} from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Categories Definition ───────────────────────────────────────────────────

const CATEGORIES = [
  {
    id: "sales",
    label: "Sales & Receipts",
    types: ["sales_invoice", "sales_receipt", "quotation"],
  },
  {
    id: "repairs",
    label: "Repairs & Service",
    types: ["repair_invoice", "repair_job_note", "repair_job_label"],
  },
  {
    id: "barcodes",
    label: "Barcodes & Labels",
    types: ["product_barcode", "serial_barcode"],
  },
  {
    id: "purchasing",
    label: "Purchasing & Inventory",
    types: [
      "purchase_order",
      "goods_received_note",
      "stock_transfer_document",
    ],
  },
  {
    id: "reports",
    label: "Reports",
    types: [
      "product_stock_report",
      "sales_summary_report",
      "parts_usage_report",
      "daily_cash_balance_report",
      "expense_report",
      "profit_loss_report",
      "stock_transfer_report",
      "customerwise_sales_report",
      "supplierwise_expense_report",
      "inventory_movement_report",
      "sales_invoice_report",
    ],
  },
  {
    id: "emails",
    label: "Email Notifications",
    types: [
      "email_header",
      "email_footer",
      "email_customer_registration",
      "email_low_stock_alert",
      "email_repair_status_update",
      "email_task_reminder",
      "email_new_admin_account",
      "email_sale_invoice",
      "email_password_reset_otp",
    ],
  },
];

// Static component to render an icon based on template type
function TemplateTypeIcon({ template, className }) {
  const type = template?.type || "";
  if (type.startsWith("email_") || template?.channel === "email") {
    return <Mail className={className} />;
  }
  if (type.includes("barcode") || type.includes("label")) {
    return <Tag className={className} />;
  }
  if (type.endsWith("_report")) {
    return <BarChart3 className={className} />;
  }
  if (type.includes("receipt") || type.includes("invoice") || type.includes("quotation")) {
    return <Receipt className={className} />;
  }
  if (type.includes("repair")) {
    return <Wrench className={className} />;
  }
  return <FileText className={className} />;
}

// ─── Elevated Modern Template Card ──────────────────────────────────────────

function TemplateCard({
  template,
  onPreview,
  onEdit,
  onClone,
  onDelete,
  canCreate,
  canUpdate,
  canDelete,
}) {
  const isSystem = Boolean(template.is_system);

  return (
    <Card
      onClick={() => {
        if (isSystem) {
          onPreview(template);
        } else if (canUpdate) {
          onEdit(template);
        } else {
          onPreview(template);
        }
      }}
      className="group cursor-pointer rounded-xl border border-border/70 hover:border-border hover:shadow-md transition-all duration-200 bg-card overflow-hidden h-[160px] flex flex-col justify-between"
    >
      <CardContent className="p-4 flex flex-col justify-between h-full">
        {/* Top Header: Icon + Name + System Badge */}
        <div className="flex items-start justify-between gap-2.5">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="h-9 w-9 rounded-lg bg-muted/60 flex items-center justify-center text-muted-foreground group-hover:text-primary group-hover:bg-primary/10 transition-colors shrink-0">
              <TemplateTypeIcon template={template} className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <h4
                className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors truncate"
                title={template.name}
              >
                {template.name}
              </h4>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {template.type_label}
              </p>
            </div>
          </div>
          {isSystem && (
            <Badge
              variant="outline"
              className="text-[10px] font-normal text-muted-foreground px-1.5 py-0.5 bg-muted/40 border-border/60 shrink-0"
            >
              Default
            </Badge>
          )}
        </div>

        {/* Middle description or context line */}
        <div className="my-auto py-1">
          <p className="text-xs text-muted-foreground/80 line-clamp-1">
            {template.description ||
              (isSystem
                ? "Standard system document template"
                : `Custom ${template.type_label?.toLowerCase() || "template"}`)}
          </p>
        </div>

        {/* Bottom: Status & Actions */}
        <div className="flex items-center justify-between pt-2.5 border-t border-border/50 text-xs">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <span
              className={cn(
                "h-2 w-2 rounded-full",
                template.is_active ? "bg-emerald-500" : "bg-muted-foreground/40"
              )}
            />
            <span className="text-[11px]">
              {isSystem ? "System Default" : template.is_active ? "Active" : "Inactive"}
            </span>
          </div>

          <div className="flex items-center gap-1">
            {isSystem ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs font-medium text-muted-foreground hover:text-primary hover:bg-primary/10 gap-1.5 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview(template);
                  }}
                  title="Preview system default template"
                >
                  <Eye className="h-3.5 w-3.5" />
                  <span>Preview</span>
                </Button>
                {canCreate && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground font-medium gap-1 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClone(template);
                    }}
                    title="Clone template into a new editable template"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    <span>Clone</span>
                  </Button>
                )}
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview(template);
                  }}
                  title="Preview template"
                >
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                {canCreate && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClone(template);
                    }}
                    title="Clone template into a new editable template"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canUpdate && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-primary cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(template);
                    }}
                    title="Edit template"
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10 cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(template);
                    }}
                    title="Delete template"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Templates() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    if (!permissions) return false;
    if (Array.isArray(permission)) return permission.some((p) => permissions.includes(p));
    return permissions.includes(permission);
  };

  const canReadTemplates = hasPermission("template read");
  const canCreateTemplates = hasPermission("template create");
  const canUpdateTemplates = hasPermission("template update");
  const canDeleteTemplates = hasPermission("template delete");

  useEffect(() => {
    if (!canReadTemplates) {
      navigate(all_routes.dashboard);
    }
  }, [canReadTemplates, navigate]);

  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [originFilter, setOriginFilter] = useState("all"); // "all" | "system" | "custom"
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [deleteTemplate, setDeleteTemplate] = useState(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["templates"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.TEMPLATES.TEMPLATES);
      return res.data?.data ?? res.data ?? [];
    },
    enabled: canReadTemplates,
  });

  useQuery({
    queryKey: ["template-types"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.TEMPLATES.TYPES);
      return res.data?.data ?? res.data ?? [];
    },
    enabled: canReadTemplates,
    staleTime: 5 * 60 * 1000,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      if (!canDeleteTemplates) {
        throw new Error("You do not have permission to delete templates");
      }
      const res = await apiClient.delete(
        API_ENDPOINTS.TEMPLATES.TEMPLATE_DETAILS(id)
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast.success("Template deleted successfully");
      setDeleteTemplate(null);
    },
    onError: (error) => {
      const msg =
        error.response?.data?.message || "Failed to delete template";
      toast.error(msg);
    },
  });

  const handleDelete = (template) => {
    if (!canDeleteTemplates) {
      toast.error("You do not have permission to delete templates");
      return;
    }
    if (template.is_system) {
      toast.error("System templates cannot be deleted.");
      return;
    }
    setDeleteTemplate(template);
  };

  const handleConfirmDelete = () => {
    if (!deleteTemplate) return;
    if (!canDeleteTemplates) {
      toast.error("You do not have permission to delete templates");
      setDeleteTemplate(null);
      return;
    }
    if (deleteTemplate.is_system) {
      toast.error("System templates cannot be deleted.");
      setDeleteTemplate(null);
      return;
    }
    deleteMutation.mutate(deleteTemplate.id);
  };

  // Filter and sort templates (system defaults first, then alphabetically)
  const filteredTemplates = useMemo(() => {
    return templates
      .filter((t) => {
        const matchSearch =
          !search ||
          t.name.toLowerCase().includes(search.toLowerCase()) ||
          (t.type_label || "").toLowerCase().includes(search.toLowerCase());
        const matchStatus =
          statusFilter === "all" ||
          (statusFilter === "active" ? t.is_active : !t.is_active);
        const matchOrigin =
          originFilter === "all" ||
          (originFilter === "system" ? t.is_system : !t.is_system);
        return matchSearch && matchStatus && matchOrigin;
      })
      .sort((a, b) => {
        if (a.is_system && !b.is_system) return -1;
        if (!a.is_system && b.is_system) return 1;
        return a.name.localeCompare(b.name);
      });
  }, [templates, search, statusFilter, originFilter]);

  // Compute category item counts
  const categoryCounts = useMemo(() => {
    const counts = { all: templates.length };
    CATEGORIES.forEach((cat) => {
      counts[cat.id] = templates.filter((t) =>
        cat.types.includes(t.type)
      ).length;
    });
    const knownTypes = CATEGORIES.flatMap((c) => c.types);
    counts["other"] = templates.filter(
      (t) => !knownTypes.includes(t.type)
    ).length;
    return counts;
  }, [templates]);

  // Group filtered templates into categorized sections
  const categorizedSections = useMemo(() => {
    const sections = CATEGORIES.map((cat) => ({
      ...cat,
      templates: filteredTemplates.filter((t) => cat.types.includes(t.type)),
    }));

    const knownTypes = CATEGORIES.flatMap((c) => c.types);
    const otherTemplates = filteredTemplates.filter(
      (t) => !knownTypes.includes(t.type)
    );

    if (otherTemplates.length > 0) {
      sections.push({
        id: "other",
        label: "Other Templates",
        templates: otherTemplates,
      });
    }

    return sections;
  }, [filteredTemplates]);

  // Templates to display on the current active tab
  const activeTabTemplates = useMemo(() => {
    if (activeTab === "all") return filteredTemplates;
    const cat = categorizedSections.find((c) => c.id === activeTab);
    return cat ? cat.templates : [];
  }, [activeTab, filteredTemplates, categorizedSections]);

  const handleEdit = (template) => {
    navigate(all_routes.editTemplate.replace(":id", template.id));
  };

  const handleClone = (template) => {
    if (!canCreateTemplates) {
      toast.error("You do not have permission to create templates");
      return;
    }
    navigate(all_routes.addTemplate, {
      state: {
        cloneTemplate: {
          name: `${template.name} (Copy)`,
          type: template.type,
          content: template.content || "",
          styles: template.styles || "",
          store_id: template.store_id || null,
          is_active: true,
        },
      },
    });
  };

  const handleResetFilters = () => {
    setSearch("");
    setActiveTab("all");
    setStatusFilter("all");
    setOriginFilter("all");
  };

  if (!canReadTemplates) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Templates"
          subtitle="Manage document layouts, receipts, invoices, barcodes, and email notifications"
        />
        <PageHeaderActions>
          {canCreateTemplates && (
            <Button
              onClick={() => navigate(all_routes.addTemplate)}
              className="gap-2 cursor-pointer"
            >
              <PlusCircle className="h-4 w-4" />
              Add Template
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      {/* Filter and Search Bar */}
      <FilterCard>
        <FilterSearch
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search templates by name, type…"
        />
        <Select value={originFilter} onValueChange={setOriginFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All templates" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All templates</SelectItem>
            <SelectItem value="system">System defaults</SelectItem>
            <SelectItem value="custom">User created</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        {(search || statusFilter !== "all" || originFilter !== "all" || activeTab !== "all") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetFilters}
            className="gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Reset
          </Button>
        )}
      </FilterCard>

      {/* Standard System Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="w-full space-y-5"
      >
        <div className="overflow-x-auto pb-1 scrollbar-none">
          <TabsList className="h-9 inline-flex w-auto bg-muted/60 p-1">
            <TabsTrigger value="all" className="text-xs px-3">
              All ({templates.length})
            </TabsTrigger>
            {CATEGORIES.map((cat) => {
              const count = categoryCounts[cat.id] || 0;
              if (count === 0 && templates.length > 0) return null;
              return (
                <TabsTrigger key={cat.id} value={cat.id} className="text-xs px-3">
                  {cat.label} ({count})
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {/* Content Area */}
        {isLoading ? (
          <div className="flex justify-center items-center py-20">
            <Spinner />
          </div>
        ) : activeTabTemplates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed rounded-lg bg-card/50">
            <FileText className="h-8 w-8 text-muted-foreground/40 mb-2" />
            <h4 className="text-sm font-medium text-foreground">
              No templates found
            </h4>
            <p className="text-xs text-muted-foreground mt-1 max-w-sm">
              No templates match your search or filter settings.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetFilters}
              className="mt-4 gap-1.5 text-xs cursor-pointer"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Reset Filters
            </Button>
          </div>
        ) : activeTab === "all" ? (
          /* "All" Tab: Clean Section Grouping without Loud Icons/Colors */
          <div className="space-y-7">
            {categorizedSections
              .filter((sec) => sec.templates.length > 0)
              .map((sec) => (
                <div key={sec.id} className="space-y-3">
                  <div className="flex items-center gap-2 border-b border-border/40 pb-1.5">
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {sec.label}
                    </h3>
                    <span className="text-xs text-muted-foreground font-normal">
                      ({sec.templates.length})
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                    {sec.templates.map((template) => (
                      <TemplateCard
                        key={template.id}
                        template={template}
                        onPreview={setPreviewTemplate}
                        onEdit={handleEdit}
                        onClone={handleClone}
                        onDelete={handleDelete}
                        canCreate={canCreateTemplates}
                        canUpdate={canUpdateTemplates}
                        canDelete={canDeleteTemplates}
                      />
                    ))}
                  </div>
                </div>
              ))}
          </div>
        ) : (
          /* Specific Category Tab: Direct Grid */
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
            {activeTabTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onPreview={setPreviewTemplate}
                onEdit={handleEdit}
                onClone={handleClone}
                onDelete={handleDelete}
                canCreate={canCreateTemplates}
                canUpdate={canUpdateTemplates}
                canDelete={canDeleteTemplates}
              />
            ))}
          </div>
        )}
      </Tabs>

      {/* Preview modal */}
      <TemplatePreviewModal
        template={previewTemplate}
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onDelete={handleDelete}
      />

      {/* Delete Confirmation Modal */}
      <AlertDialog
        open={!!deleteTemplate}
        onOpenChange={(open) => {
          if (!open) setDeleteTemplate(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete template{" "}
              <strong className="text-foreground">
                "{deleteTemplate?.name}"
              </strong>
              ? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmDelete();
              }}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? (
                <>
                  <Spinner className="h-4 w-4 mr-2" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
